import { NoteIndexEntry } from '~/modules/songs/utils/note-index';

export type DifficultyLevel = 'Easy' | 'Medium' | 'Hard' | 'Expert';

export interface SongDifficulty {
  level: DifficultyLevel;
  /** 0-100, for comparing two songs rather than for showing to anyone. */
  score: number;
  factors: {
    /** Notes per second of actual singing. */
    density: number;
    /** Semitones between the lowest and highest note. */
    span: number;
    /** Average note length in beats - shorter means the line moves faster. */
    noteLength: number;
  };
}

/**
 * Anchors taken from the shipped catalogue rather than invented: the low end sits near the 10th
 * percentile of 6243 songs and the high end near the 95th, so a song scored "Hard" is hard
 * relative to what is actually available to sing.
 */
const DENSITY_EASY = 2.5;
const DENSITY_HARD = 8;
const SPAN_EASY = 10;
const SPAN_HARD = 30;
/** Note length runs the other way - shorter notes are harder. */
const NOTE_LENGTH_EASY = 8;
const NOTE_LENGTH_HARD = 2;

const WEIGHTS = { density: 0.45, span: 0.3, noteLength: 0.25 };

/** Where `value` sits between two anchors, clamped, so outliers cannot leave the 0-1 range. */
const normalise = (value: number, easy: number, hard: number) =>
  Math.max(0, Math.min(1, (value - easy) / (hard - easy)));

const LEVELS: Array<{ level: DifficultyLevel; min: number }> = [
  { level: 'Expert', min: 70 },
  { level: 'Hard', min: 50 },
  { level: 'Medium', min: 30 },
  { level: 'Easy', min: 0 },
];

/**
 * How demanding a song's chart is. Measured from the notes themselves - how fast they come, how
 * far the line reaches, and how quickly it moves - not guessed from tempo or any other number
 * that happens to be available.
 */
export const calculateSongDifficulty = (entry: NoteIndexEntry): SongDifficulty => {
  // A chart with no singing time has nothing measurable about it; `secondsSung` of 0 would also
  // divide.
  const density = entry.secondsSung > 0 ? entry.notes / entry.secondsSung : 0;

  const factors = { density, span: entry.span, noteLength: entry.averageNoteLength };

  const score = Math.round(
    100 *
      (WEIGHTS.density * normalise(density, DENSITY_EASY, DENSITY_HARD) +
        WEIGHTS.span * normalise(entry.span, SPAN_EASY, SPAN_HARD) +
        WEIGHTS.noteLength * normalise(entry.averageNoteLength, NOTE_LENGTH_EASY, NOTE_LENGTH_HARD)),
  );

  return { level: LEVELS.find(({ min }) => score >= min)!.level, score, factors };
};
